import toast from 'react-hot-toast'
import { GTDialog, GTDialogBody } from '@/components/ui/gt-dialog'
import { cn } from '@/lib/utils'
import { CalendarPlus, Feather, LucideIcon, Pause, Sparkles } from 'lucide-react'
import { DateTime } from 'luxon'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { GOAL_PAUSE_PATCH, TGoal, TGoalPatch } from './goalTypes'

/**
 * The repair moment (GOALS-V1-SCOPE §Recovery): one dialog, up to four honest choices, each a
 * real change to the plan the server derives status from. Framing rules — open with what's
 * still TRUE, never with what failed; resurface the user's own why verbatim. Neutral/warm
 * styling throughout: off-track knocks, it never shames.
 */

const EXTEND_WEEKS = 4

export const weeksLeft = (goal: TGoal): number | null =>
    goal.endDate ? Math.max(0, Math.ceil(DateTime.fromISO(goal.endDate).diff(DateTime.local(), 'weeks').weeks)) : null

/** Derive the repair opener from the real runway. Always leads with what's still true. */
const openerLine = (goal: TGoal): string => {
    const left = weeksLeft(goal)
    if (left === null) return 'No deadline — there’s still room to land this.'
    if (left === 0) return 'This is the last week — one good week still counts.'
    return `${left} week${left === 1 ? '' : 's'} left — this is still in reach.`
}

/** "In May you said" — the month the goal was started. */
const startMonthPhrase = (goal: TGoal): string => {
    const start = goal.startDate ? DateTime.fromISO(goal.startDate) : null
    return start?.isValid ? `In ${start.toFormat('MMMM')} you said` : 'When you set this you said'
}

const unitSuffix = (goal: TGoal) => (goal.unit === 'hrs' ? ' hrs' : '×')

export interface TRepairOption {
    /** Stable name for the change, so the weekly review can offer a subset and record which was made. */
    key: 'ease' | 'extend' | 'keep' | 'pause'
    icon: LucideIcon
    title: string
    description: string
    toastMessage: string
    patch: TGoalPatch
}

/** Only the options that would change something for this goal. Each re-commitment also starts the
 * quiet-week count fresh, so the new plan is judged from here on. */
export const repairOptions = (goal: TGoal): TRepairOption[] => {
    const options: TRepairOption[] = []
    const target = goal.weeklyTarget ?? 0
    const eased =
        goal.unit === 'hrs' ? Math.max(0.5, Math.round(target * 0.66 * 2) / 2) : Math.max(1, Math.round(target * 0.66))
    if (target > 0 && eased < target) {
        options.push({
            key: 'ease',
            icon: Feather,
            title: 'Ease back',
            description: `${target}${unitSuffix(goal)} → ${eased}${unitSuffix(goal)} a week; the endgame stays.`,
            toastMessage: `Weekly target is now ${eased}${unitSuffix(goal)}`,
            patch: { weeklyTarget: eased, forgive: true },
        })
    }
    if (goal.endDate) {
        const extended = DateTime.fromISO(goal.endDate).plus({ weeks: EXTEND_WEEKS })
        options.push({
            key: 'extend',
            icon: CalendarPlus,
            title: 'Extend the runway',
            description: `New end date ${extended.toFormat('MMM d')}; grace recomputes from it.`,
            toastMessage: `Runway extended to ${extended.toFormat('MMM d')}`,
            patch: { endDate: extended.toISODate(), forgive: true },
        })
    }
    options.push(
        {
            key: 'keep',
            icon: Sparkles,
            title: 'Keep this version',
            description: 'Past quiet weeks stop counting; your history stays.',
            toastMessage: 'Keeping this version',
            patch: { forgive: true },
        },
        {
            key: 'pause',
            icon: Pause,
            title: 'Pause',
            description: 'No pace and no penalty until you resume.',
            toastMessage: 'Paused — no penalty. Resume when ready.',
            patch: GOAL_PAUSE_PATCH,
        }
    )
    return options
}

/** A self-graded goal's status is its owner's call, so choosing a repair is that call. */
export const repairPatch = (goal: TGoal, option: TRepairOption): TGoalPatch =>
    goal.progressMode === 'manual' && !option.patch.status ? { ...option.patch, status: 'on_track' } : option.patch

const RepairDialog = () => {
    const { allGoals, repairGoalId, setRepairGoalId, updateGoal } = useGoalCreation()
    const goal = allGoals.find((g) => g.id === repairGoalId)

    const choose = (option: TRepairOption) => {
        if (!goal) return
        updateGoal(goal.id, repairPatch(goal, option))
        toast.success(option.toastMessage)
        setRepairGoalId(null)
    }

    return (
        <GTDialog
            open={!!goal}
            onOpenChange={(next) => !next && setRepairGoalId(null)}
            label="Goal check-in"
            description="Choose how to get this goal back on track."
        >
            {goal && (
                <GTDialogBody className="gap-3">
                    <h2 className="text-title-md leading-snug text-foreground">{openerLine(goal)}</h2>
                    {goal.why && (
                        <blockquote className="border-l-2 border-border pl-3 text-body-md text-secondary-foreground">
                            {startMonthPhrase(goal)}: “{goal.why}” — still true?
                        </blockquote>
                    )}
                    {goal.contractLine && <p className="text-body-sm text-muted-foreground">{goal.contractLine}</p>}

                    <div className="mt-1 flex flex-col gap-0.5" role="group" aria-label="Repair options">
                        {repairOptions(goal).map((option) => (
                            <button
                                key={option.title}
                                type="button"
                                onClick={() => choose(option)}
                                className={cn(
                                    '-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-lg px-2 py-2.5 text-left',
                                    'transition-[background-color,transform] duration-150 hover:bg-muted active:scale-[0.96]',
                                    'focus-visible:bg-muted focus-visible:outline-none'
                                )}
                            >
                                <div className="mt-px flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                    <option.icon className="size-4" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-body-sm font-medium text-foreground">{option.title}</p>
                                    <p className="mt-0.5 text-body-sm text-muted-foreground">{option.description}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </GTDialogBody>
            )}
        </GTDialog>
    )
}

export default RepairDialog
